<script lang="ts" setup>
import type { IProps } from './UiNewsletter.types'
import { useNewsletter } from './useNewsletter'

defineProps<IProps>()

const { isTrueDesktop: isDesktop } = useBSBreakpoints()
const {
  form,
  submitForm,
  validateCheckbox,
  validateEmail,
  validateName } = useNewsletter()

const expand = ref(false)
const { appURL } = useRuntimeConfig().public
</script>

<template>
  <div
    id="newsletter"
    class="newsletter"
  >
    <div :class="['newsletter__wrapper', `newsletter__wrapper--${version}`]">
      <div class="newsletter__box">
        <h3
          v-if="version !== 'primary'"
          :class="['newsletter__header', `newsletter__header--${version}`]"
        >
          {{ title }}
        </h3>

        <h3
          v-else-if="version === 'primary'"
          class="newsletter__header"
        >
          {{ title }}
        </h3>

        <p :class="[version === 'primary' ? 'newsletter__text' : 'newsletter__textSecondary']">
          {{ subtitle }}
        </p>
      </div>

      <form
        class="newsletter__form"
        action="https://kontakt.benefitsystems.pl/add_subscriber.html"
        accept-charset="utf-8"
        method="POST"
        @submit.prevent="submitForm"
      >
        <div class="newsletter__input_wrapper">
          <div
            :class="[
              'newsletter__input',
              'newsletter__input_name',
              { 'error' : form.name.error }
            ]"
          >
            <div>
              <input
                v-model="form.name.value"
                type="text"
                name="name"
                placeholder="Twoje imię"
                @blur="validateName"
                @keyup="validateName"
              >
              <span
                v-if="form.name.error"
                class="error"
              >
                {{ form.name.error }}
              </span>
            </div>
          </div>
          <div
            :class="[
              'newsletter__input',
              'newsletter__input_email',
              { 'error' : form.email.error }
            ]"
          >
            <input
              v-model="form.email.value"
              type="text"
              name="email"
              placeholder="Twój email"
              class="newsletter__input--input"
              @blur="validateEmail"
              @keyup="validateEmail"
            >
            <input
              v-if="isDesktop"
              class="newsletter__button"
              type="submit"
              :value="$t('newsletter.submit')"
            >
            <span
              v-if="form.email.error"
              class="error"
            >
              {{ form.email.error }}
            </span>
          </div>
        </div>
        <div
          :class="[
            'newsletter__checkbox',
            { 'error' : form.checkbox.error }, `newsletter__checkbox--${version}`
          ]"
        >
          <input
            id="webform_consent#8"
            v-model="form.checkbox.value"
            type="checkbox"
            :name="checkboxName"
            value="true"
            @change="validateCheckbox"
          >
          <label
            for="webform_consent#8"
          >
            <span
              :class="['newsletter__consent', expand && 'newsletter__consent--expand']"
              v-html="consent"
            />
            <transition
              v-if="extend"
              name="expand"
            >
              <div v-show="expand">
                <span
                  class="newsletter__extend"
                  v-html="extend"
                />
              </div>
            </transition>

            <button
              v-if="extend"
              class="newsletter__showMore"
              @click.prevent="expand = !expand"
            >
              {{ expand ? $t('common.closeMore') : $t('common.showMore') }}
              <img
                :class="[
                  'newsletter__arrow',
                  expand && 'newsletter__arrow--expand'
                ]"
                width="24px"
                height="24px"
                src="/icons/arrow_down.svg"
                alt=""
              >
            </button>
          </label>
        </div>
        <input
          type="hidden"
          name="campaign_token"
          :value="campaignToken"
        >

        <input
          type="hidden"
          name="thankyou_url"
          :value="`${appURL}${thankYouUrl}`"
        >

        <input
          type="hidden"
          name="start_day"
          value="0"
        >

        <input
          type="hidden"
          name="custom_form_id"
          :value="customFormId"
        >

        <input
          v-if="!isDesktop"
          class="newsletter__button"
          type="submit"
          :value="$t('newsletter.submit')"
        >
      </form>
    </div>
  </div>
</template>

<style lang="scss" scoped>

.newsletter {
  $root: &;

  padding: 4rem 2.4rem;

  &__showMore {
    @include body-2-medium;
    display: flex;
    align-items: center;
    gap: .8rem;
    margin-top: 1.2rem;
    color: $text-400;
    font-weight: 700;
  }

  &__consent {
    @include body-2-medium;
    display: block;
    color: $text-400;

    :deep(p) {
      margin-bottom: 1.2rem;
    }
  }

  &_wrapper {
    position: relative;
  }

  &__extend {
    @include body-2-medium;
    display: block;
    margin-top: 1.2rem;
    color: $text-400;

    :deep(a) {
      color: $blue-400;
      text-decoration: none;
    }

    :deep(p) {
      padding-bottom: 1.2rem;
    }

    :deep(b) {
      font-weight: bold;
    }
  }

  &__arrow {
    width: 22px;
    height: 22px;
    transform: rotate(0);
    transition: transform 0.5s;

    &--expand {
      transform: rotate(180deg);
    }
  }

  @include atM {
    padding: 6.4rem 2.4rem;
  }

  &__wrapper {
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 3.2rem;
    max-width: 134rem;
    width: 100%;
    margin-right: auto;
    margin-left: auto;

    @include atM {
      flex-direction: row;
      gap: 5rem;

      &--primary {
        align-items: center;
      }
    }
  }

  &__form {
    display: flex;
    flex-direction: column;

    @include atM {
      width: 50%;
    }
  }

  &__box {
    @include atM {
      width: 50%;
    }
  }

  &__header {
    margin-bottom: 1.6rem;
    font-weight: 500;
    font-size: 2.8rem;
    line-height: 3.6rem;
    white-space: pre-wrap;

    @include atS {
      font-size: 3.8rem;
      line-height: 5.1rem;
    }

    @include atM {
      margin-bottom: 2.4rem;
      font-size: 4rem;
      line-height: 4.8rem;
    }

    @include atXL {
      font-size: 4.8rem;
      line-height: 5.6rem;
    }

    &--secondary {
      max-width: 750px;
      width: 100%;
      color: $text-400;
      font-weight: 500;
      font-size: 3.8rem;
      line-height: 5.1rem;

      @include atS {
        font-size: 3.8rem;
        line-height: 5.1rem;
      }

      @include atM {
        @include h5;
        margin-bottom: 2.4rem;
      }
    }
  }

  &__text {
    color: $text-300;
    @include body-1-medium;

    @include atM {
      font-size: 1.8rem;
      line-height: 2.4rem;
    }

    @include atXL {
      font-size: 2rem;
      line-height: 2.8rem;
    }
  }

  &__textSecondary {
    @include body-1-medium;
    color: $text-400;

    @include atM {
      @include h9;
      height: 100%;
    }
  }

  &__input {
    position: relative;
    display: flex;
    align-items: center;
    width: 100%;
    height: 4.8rem;
    margin-bottom: 3rem;
    padding-left: 4.6rem;
    background-color: $gray-200;
    background-repeat: no-repeat;
    background-position: 2rem;
    border-radius: 5rem;

    &--input {
      margin-right: 2rem;
    }

    &_name {
      background-image: url('/icons/icon_user.svg');
      &.error {
        bottom: 1.2rem;
        border: 0.1rem solid red;
      }
    }

    &_email {
      background-image: url('/icons/icon_send_email.svg');
      &.error {
        bottom: 1.2rem;
        border: 0.1rem solid red;
      }

      @include atM {
        padding-right: 14rem;
        overflow: visible;

        .newsletter__button {
          position: absolute;
          top: 50%;
          right: 0;
          width: auto;
          height: 5.6rem;
          transform: translateY(-50%);
        }
      }
    }

    &_wrapper {
      @include atXXS {
        display: flex;
        flex-direction: row;
        gap: 2.4rem;
      }

      @include atM {
        position: relative;
        display: flex;
        align-items: center;
        gap: 1.6rem;

        #{$root}__input {
          flex: 1;
        }
      }

      span.error {
        position: absolute;
        bottom: -2rem;
        left: 2.6rem;
        display: inline-block;
        color: red;
      }
    }

    @include atM {
      height: 5.6rem;
    }

    input {
      width: 100%;
      height: 100%;
      color: $text-400;
      font-size: 1.6rem;
      line-height: 3rem;

      &[type=submit] {
        min-width: 15rem;
        width: auto;
        color: $white;
      }

      &::-webkit-input-placeholder {
        color: $text-400;
        font-size: 1.6rem;
        line-height: 3rem;
      }

      &:-ms-input-placeholder {
        color: $text-400;
        font-size: 1.6rem;
        line-height: 3rem;
      }

      &::placeholder {
        color: $text-400;
        font-size: 1.6rem;
        line-height: 3rem;
      }
    }
  }

  &__checkbox {
    position: relative;
    display: flex;
    margin-bottom: 2.4rem;
    font-size: 1.4rem;
    line-height: 2rem;

    input {
      min-width: 2.4rem;
      width: 2.4rem;
      height: 2.4rem;
      margin-right: 0.8rem;
      padding: 0.4rem;
      border: 0.1rem solid $text-400;
      border-radius: 0.4rem;

      &:checked {
        background-image: url('/icons/icon_check.svg');
        background-repeat: no-repeat;
        background-position: center;
      }
    }

    &.error {
      input {
        border: 0.1rem solid red;
      }

      span.error {
        position: absolute;
        top: 2.6rem;
        color: red;
        font-size: 1rem;
      }
    }

    :deep() {
      .newsletter__link--primary {
        color: $blue-400;
      }

      .newsletter__link--secondary {
        color: $purple-400;
      }
    }
  }

  &__button {
    align-self: center;
    width: 100%;
    padding: 1.4rem 2.4rem;
    color: $white;
    font-size: 1.4rem;
    line-height: 2rem;
    text-align: center;
    background-color: $text-400;
    border-radius: 5rem;

    @include atXS {
      width: 33%;
      margin-right: auto;
    }

    @include atM {
      width: auto;
    }
  }
}
</style>
